import React, { createContext, useContext } from "react";
import { PlanProviderDataMapping } from "./PlanProvider.hooks";
import {
    ApplicationFeatures,
    PlanFeatures,
    ProcessingActivityTemplates,
    StandardGDPRActivities,
    StandardNIS2Activities,
    SystemTemplates,
} from "./PlanProvider.types";
import { DotLegalFullScreenSpinner } from "@dotlegal/dotlegal-ui-components";

export interface IPlanProvider {
    children: JSX.Element;
}

function PlanProvider(props: IPlanProvider) {
    const { data, isLoading } = PlanProviderDataMapping();

    return (
        <PlanContextContext.Provider value={data}>
            {isLoading ? <DotLegalFullScreenSpinner /> : <React.Fragment>{props.children}</React.Fragment>}
        </PlanContextContext.Provider>
    );
}

export function usePlanContext(): PlanFeatures {
    return useContext(PlanContextContext)!.planFeatures;
}

export function useFeatureFlags() {
    const featureFlags = useContext(PlanContextContext)!.featureFlags;
    const isEnabled = (featureFlag: string) => featureFlags[featureFlag];

    return {
        isEnabled,
    };
}

export const PlanContextContext = createContext<ApplicationFeatures | undefined>({
    planFeatures: {
        platformFeatures: {
            canDownloadPolicyTemplates: false,
            maxNumberOfGroupCompanies: undefined,
            dashboard: false,
            maxNumberOfPolicies: undefined,
            maxNumberOfProcesses: undefined,
            commentModule: false,
            addTasksToOutlook: false,
            annualWheelOverview: false,
            annualWheelReport: false,
            annualWheelCopyActivity: false,
            hasTaskManagementAccess: false,
            taskHistory: false,
            assignTask: false,
            signOff: false,
            annualWheelComplianceArea: false,
            riskVersions: false,
            tags: false,
            relatedActivitiesAndTasks: false,
            auditLog: false,
            canAccessRelationsBetweenEntities: false,
            canManagePageView: false,
            canSaveTableConfiguration: false,
            canTransferUserResponsibility: false,
            canTransferBusinessAreaAccess: false,
        },
        dataProtectionFeatures: {
            maxNumberOfProcessingActivities: undefined,
            processingActivityTemplates: ProcessingActivityTemplates.MostPopular,
            processingActivityValidationFlow: false,
            dataProcessingAgreements: false,
            processingActivitiesDocuments: false,
            standardGDPRActivities: StandardGDPRActivities.NoAccess,
            maxNumberOfProcessingActivityRiskAssessments: undefined,
            maxNumberOfGDPRRiskScenarios: undefined,
            gdprRiskAssessmentConsequenceCatalogue: false,
            gdprRiskReports: false,
            maxNumberOfIncidentLogEntries: undefined,
            processingActivityStatus: false,
            registrationOfDataController: false,
            sharedResponsibility: false,
            linkPoliciesAndProcessingActivities: false,
            recordDataProcessor: false,
            recordsExport: false,
            maxNumberOfContactsOnUnit: undefined,
            standardListExport: false,
        },
        informationSecurityFeatures: {
            maxNumberOfSystems: undefined,
            hasInfoSec: false,
            systemTemplates: SystemTemplates.All,
            systemDocuments: false,
            niS2ActivitiesCatalogue: StandardNIS2Activities.NoAccess,
            isoActivitiesCatalogue: false,
            maxNumberOfAssetRiskAssessments: undefined,
            maxNumberOfNIS2RiskScenarios: undefined,
            nis2RiskAssessmentConsequenceCatalogue: false,
            niS2RiskReports: false,
            systemRiskClassification: false,
            standardListExport: false,
            storageType: false,
            entityType: false,
            dataType: false,
            certifications: false,
            entityTypeFilter: false,
            associatedAssets: false,
            internalControlProcedures: false,
            personalData: false,
            automaticRegistrationOfDataProcessorTransfers: false,
            ai: false,
            legalEntityDocuments: false,
        },
        vendorManagementFeatures: {
            hasVendorManagement: false,
            maxNumberOfVendorRiskClassificationsPerYear: undefined,
            auditGroups: false,
            internalAssessmentGroups: false,
            vendorDocuments: false,
            maxNumberOfContactsOnUnit: undefined,
            standardListsExport: false,
            certifications: false,
            processingActivityAssociateInternalAssessments: false,
            systemAssociateInternalAssessments: false,
            publicAssessments: false,
            systemClassifications: false,
            legalEntityContacts: false,
        },
        frameworkFeatures: {
            canCreateCustomFrameworks: false,
            maxNumberOfFrameworks: undefined,
        },
        addonsFeatures: {
            sso: false,
            customLists: false,
            organizationalManagement: false,
            research: false,
            declarations: false,
            userManagementPremium: false,
            cvr: false,
            obligations: false,
            entityTemplates: false,
            searchService: false,
            aiFeatures: false,
            policyManagement: false,
            enterpriseRiskManagement: false,
            publicApi: false,
        },
        contractManagementFeatures: {
            hasContractManagement: false,
            maxNumberOfContracts: undefined,
        },
    },
    featureFlags: {},
});

export default PlanProvider;
